import { EventDispatcher } from '../EventDispatcher'
import { Position } from './Position'
import { PositionTimer } from './PositionTimer'

export enum GeolocationServiceStatus {
  INITIALIZING,
  NOT_SUPPORTED,
  AWAITING_PERMISSION,
  PERMISSION_DENIED,
  READY_TO_USE,
  WATCHING,
  STOPPED,
}

enum GeolocationMethod {
  WATCH,
  GET,
}

export class GeolocationService {
  private positionDispatcher: EventDispatcher<Position>
  private statusDispatcher: EventDispatcher<GeolocationServiceStatus>
  private status: GeolocationServiceStatus =
    GeolocationServiceStatus.INITIALIZING

  private locationWatcherId?: number
  private positionTimer?: PositionTimer
  private lastPosition?: Position

  constructor() {
    this.positionDispatcher = new EventDispatcher<Position>()
    this.statusDispatcher = new EventDispatcher<GeolocationServiceStatus>()

    this.positionTimer = new PositionTimer(() => {
      this.getLocation()
    })
  }

  private triggerNewPosition(position: Position) {
    this.positionDispatcher.fire(position)
    this.lastPosition = position
  }

  private stateToStatus(
    permissionState: PermissionState,
    triggerDispatcher = true
  ) {
    switch (permissionState) {
      case 'granted':
        this.handleStatusChange(
          GeolocationServiceStatus.READY_TO_USE,
          triggerDispatcher
        )
        break
      case 'prompt':
        this.handleStatusChange(
          GeolocationServiceStatus.AWAITING_PERMISSION,
          triggerDispatcher
        )
        break
      case 'denied':
        this.handleStatusChange(
          GeolocationServiceStatus.PERMISSION_DENIED,
          triggerDispatcher
        )
        break
    }
  }

  private handleStatusChange(
    status: GeolocationServiceStatus,
    triggerDispatcher = true
  ) {
    this.status = status

    if (triggerDispatcher) {
      this.statusDispatcher.fire(status)
    }
  }

  private locationErrorHandler(
    error: GeolocationPositionError,
    method: GeolocationMethod
  ) {
    switch (error.code) {
      case GeolocationPositionError.PERMISSION_DENIED:
        console.log('Changing status to PERMISSION_DENIED')
        this.handleStatusChange(GeolocationServiceStatus.PERMISSION_DENIED)
        break
      case GeolocationPositionError.POSITION_UNAVAILABLE:
        console.log('Manually requesting location.')
        this.getLocation()
        break
      case GeolocationPositionError.TIMEOUT:
        if (method === GeolocationMethod.WATCH) {
          console.debug('Stopping watching.')
          this.stopWatching()
          console.debug('Starting watching again.')
          this.startWatching()
        } else {
          this.positionTimer?.reset()
        }
        break
    }
  }

  init() {
    if (!navigator.geolocation) {
      console.error('Geolocation is not supported by this browser.')
      this.handleStatusChange(GeolocationServiceStatus.NOT_SUPPORTED, false)
      return
    }

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((permissionStatus) => {
        this.stateToStatus(permissionStatus.state, true)

        permissionStatus.onchange = () => {
          this.stateToStatus(permissionStatus.state)
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  getStatus() {
    return this.status
  }

  getLastPosition() {
    return this.lastPosition
  }

  requestPermission() {
    if (this.status === GeolocationServiceStatus.AWAITING_PERMISSION) {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          this.triggerNewPosition(Position.fromGeolocationPosition(position)),
        (error) => {
          console.error(error)
        },
        { enableHighAccuracy: true }
      )
    } else {
      throw new Error('GeolocationService is not awaiting permission')
    }
  }

  getLocation() {
    if (
      this.status === GeolocationServiceStatus.READY_TO_USE ||
      this.status === GeolocationServiceStatus.WATCHING
    ) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.triggerNewPosition(Position.fromGeolocationPosition(position))
          this.positionTimer?.reset()
        },
        (error) => this.locationErrorHandler(error, GeolocationMethod.GET),
        {
          enableHighAccuracy: true,
          timeout: 1000,
        }
      )
    } else {
      throw new Error(
        'GeolocationService is not ready to get a location manunually'
      )
    }
  }

  startWatching() {
    if (
      (this.status === GeolocationServiceStatus.READY_TO_USE ||
        this.status === GeolocationServiceStatus.STOPPED) &&
      !this.locationWatcherId
    ) {
      this.locationWatcherId = navigator.geolocation.watchPosition(
        (position) => {
          this.triggerNewPosition(Position.fromGeolocationPosition(position))
        },
        (error) => {
          console.error('Error watching position: ', error)
          this.locationErrorHandler(error, GeolocationMethod.WATCH)
        },
        {
          enableHighAccuracy: true,
        }
      )
      this.positionTimer?.start()
      this.handleStatusChange(GeolocationServiceStatus.WATCHING)
    } else if (this.status === GeolocationServiceStatus.WATCHING) {
      throw new Error('GeolocationService is already watching')
    } else {
      throw new Error('GeolocationService is not ready to start watching')
    }
  }

  stopWatching() {
    if (this.locationWatcherId) {
      navigator.geolocation.clearWatch(this.locationWatcherId)
      this.locationWatcherId = undefined
    }

    this.positionTimer?.stop()
    this.handleStatusChange(GeolocationServiceStatus.STOPPED)
  }

  registerPositionListener(listener: (position: Position) => void) {
    this.positionDispatcher.register(listener)
  }

  registerStatusListener(listener: (status: GeolocationServiceStatus) => void) {
    this.statusDispatcher.register(listener)
  }

  destroy() {
    this.stopWatching()
    this.positionDispatcher.unregisterAll()
    this.statusDispatcher.unregisterAll()
    this.positionTimer?.stop()
  }
}
